import { map } from 'lodash';
import { navigateToProjectReports } from '../../../links/index.jsx';
import { INITIATIVE_TYPES } from '../../../../lib/schema.js';

const initiativeTypes = INITIATIVE_TYPES.map((type) => `${type}Initiatives`);

export const resetFilters = (preselectedFilters) => {
  const query = {
    // archived: false, //excluded param
    projectStartDate: null,
    projectEndDate: null,
    surveyStartDate: null,
    surveyEndDate: null,
    initiativeStartDate: null,
    initiativeEndDate: null,
    surveyStatuses: [],
    projectIds: [],
    parentPortfolioIds: [],
    portfolioIds: [],
    groups: [],
    employees: [],
    status: null,
    survey: null,
    types: [],
    owners: [],
    scope: [],
    projectImpact: [],
    initiativeImpact: [],
    communication: [],
    categories: [],
    projectStatusIds: [],
    organizationStakeholderIds: [],
    portfolioMilestoneIds: [],
    projectMilestoneIds: [],
    stakeholderInfluences: [],
    stakeholderInterests: [],
    stakeholderImpacts: [],
    stakeholderEngagementFrequencies: [],
    stakeholderSupports: [],
    ...preselectedFilters,
  };

  navigateToProjectReports(query);
};

const PARTICIPANT_CONFIG = {
  Employee: {
    getTitle: (e) => e.fullName,
    getIds: (props) => props.selectedEmployeeIds,
    getList: (props) => props.employees,
  },
  Group: {
    getTitle: (g) => g.name,
    getIds: (props) => props.selectedGroupIds,
    getList: (props) => props.groups,
  },
  Stakeholder: {
    getTitle: (s) => s.fullName,
    getIds: (props) => props.selectedStakeholdersIds,
    getList: (props) => props.orgStakeholders,
  },
};

const createEmptyProject = (
  project,
  participantType,
  additionalProps = {}
) => ({
  ...project,
  communicationInitiatives: [],
  readinessInitiatives: [],
  trainingInitiatives: [],
  sustainmentInitiatives: [],
  support:
    participantType === 'Stakeholder' ? additionalProps.support : undefined,
  engagementFrequency:
    participantType === 'Stakeholder'
      ? additionalProps.engagementFrequency
      : undefined,
});

function buildGroups(props, participantType) {
  const config = PARTICIPANT_CONFIG[participantType];
  if (!config) return {};

  const participantIds = config.getIds(props);
  const possibleParticipants = config.getList(props);

  if (!possibleParticipants || possibleParticipants.length === 0) return {};

  const selectedParticipants = participantIds.length
    ? possibleParticipants.filter(({ id }) => participantIds.includes(id))
    : possibleParticipants;

  const resultsGroups = {};
  selectedParticipants.forEach((participant) => {
    resultsGroups[participant.id] = {
      id: participant.id,
      title: config.getTitle(participant),
      participantType,
      projects: {},
    };
  });

  return resultsGroups;
}

export function groupResults(props) {
  const {
    stakeholderEngagementFrequencies,
    stakeholderImpacts,
    stakeholderInfluences,
    stakeholderInterests,
    stakeholderSupports,
  } = props.query;

  let participantType;
  if (props.selectedEmployeeIds.length > 0) {
    participantType = 'Employee';
  } else if (props.selectedGroupIds.length > 0) {
    participantType = 'Group';
  } else if (
    props.selectedStakeholdersIds.length > 0 ||
    stakeholderEngagementFrequencies ||
    stakeholderImpacts ||
    stakeholderInfluences ||
    stakeholderInterests ||
    stakeholderSupports
  ) {
    participantType = 'Stakeholder';
  } else {
    participantType = 'Group';
  }

  const resultsGroups = buildGroups(props, participantType);
  const stakeholdersWithProjects = {};

  function addInitiative(
    initiativeParticipation,
    project,
    initiativeType,
    initiative
  ) {
    if (initiativeParticipation.participantType !== participantType) return;
    const { participantId } = initiativeParticipation;
    if (!resultsGroups[participantId]) return;

    const initiativeToAdd = { ...initiative, initiativeParticipation };

    if (!resultsGroups[participantId].projects[project.id]) {
      resultsGroups[participantId].projects[project.id] = createEmptyProject(
        project,
        participantType,
        {
          support: initiativeParticipation.support,
          engagementFrequency: initiativeParticipation.engagementFrequency,
        }
      );
    }

    const groupProject = resultsGroups[participantId].projects[project.id];
    groupProject[initiativeType] = [
      ...groupProject[initiativeType],
      initiativeToAdd,
    ];
  }

  props.results.projects.forEach((project) => {
    initiativeTypes.forEach((initiativeType) => {
      project[initiativeType].forEach(
        ({
          initiativeParticipations,
          stakeholderParticipations,
          ...initiative
        }) => {
          const participations =
            participantType === 'Stakeholder'
              ? stakeholderParticipations || []
              : initiativeParticipations || [];
          participations.forEach((initiativeParticipation) => {
            addInitiative(
              initiativeParticipation,
              project,
              initiativeType,
              initiative
            );
          });
        }
      );
    });

    if (participantType === 'Stakeholder') {
      (project.stakeholderProfiles || []).forEach((stakeholderProfile) => {
        const stakeholderId = stakeholderProfile.organizationStakeholderId;

        if (resultsGroups[stakeholderId]) {
          if (!stakeholdersWithProjects[stakeholderId]) {
            stakeholdersWithProjects[stakeholderId] = {
              id: stakeholderId,
              participantType: 'Stakeholder',
              projects: [],
            };
          }

          stakeholdersWithProjects[stakeholderId].projects.push(
            createEmptyProject(project, participantType, {
              support: stakeholderProfile.support,
              engagementFrequency: stakeholderProfile.engagementFrequency,
            })
          );
        }
      });
    }
  });

  Object.keys(stakeholdersWithProjects).forEach((stakeholderId) => {
    if (!resultsGroups[stakeholderId]) {
      resultsGroups[stakeholderId] = stakeholdersWithProjects[stakeholderId];
    } else {
      resultsGroups[stakeholderId].projects = [
        ...Object.values(resultsGroups[stakeholderId].projects),
        ...stakeholdersWithProjects[stakeholderId].projects,
      ];
    }
  });

  const result = map(resultsGroups, (group) => ({
    ...group,
    projects: Object.values(group.projects),
  })).filter(({ projects }) => projects.length);

  return result;
}

// old code dont delete
// function buildGroups(props, participantType) {
//   const {
//     employees,
//     groups,
//     orgStakeholders,
//     selectedEmployeeIds,
//     selectedGroupIds,
//     selectedStakeholdersIds,
//   } = props;
//
//   let getTitle;
//   let participantIds;
//   let possibleParticipants;
//
//   if (participantType === 'Employee') {
//     getTitle = (employee) => employee.fullName;
//     participantIds = selectedEmployeeIds;
//     possibleParticipants = employees;
//   } else if (participantType === 'Group') {
//     getTitle = (group) => group.name;
//     participantIds = selectedGroupIds;
//     possibleParticipants = groups;
//   } else if (participantType === 'Stakeholder') {
//     getTitle = (stakeholder) => stakeholder.fullName;
//     participantIds = selectedStakeholdersIds;
//     possibleParticipants = orgStakeholders;
//   }
//
//   const resultsGroups = {};
//   const selectedParticipants = participantIds.length
//     ? possibleParticipants.filter(({ id }) => participantIds.includes(id))
//     : possibleParticipants;
//
//   (selectedParticipants || []).forEach((participant) => {
//     resultsGroups[participant.id] = {
//       id: participant.id,
//       title: getTitle(participant),
//       participantType,
//       projects: {},
//     };
//   });
//   return resultsGroups;
// }
//
// export function groupResults(props) {
//   const {
//     stakeholderEngagementFrequencies,
//     stakeholderImpacts,
//     stakeholderInfluences,
//     stakeholderInterests,
//     stakeholderSupports,
//   } = props.query;
//   let participantType;
//   if (props.selectedEmployeeIds.length > 0) {
//     participantType = 'Employee';
//   } else if (props.selectedGroupIds.length > 0) {
//     participantType = 'Group';
//   } else if (
//     props.selectedStakeholdersIds.length > 0 ||
//     stakeholderEngagementFrequencies ||
//     stakeholderImpacts ||
//     stakeholderInfluences ||
//     stakeholderInterests ||
//     stakeholderSupports
//   ) {
//     participantType = 'Stakeholder';
//   } else participantType = 'Group';
//
//   const resultsGroups = buildGroups(props, participantType);
//
//   function addInitiative(
//     initiativeParticipation,
//     project,
//     initiativeType,
//     initiative
//   ) {
//     if (initiativeParticipation.participantType !== participantType) return;
//     const { participantId } = initiativeParticipation;
//
//     if (!resultsGroups[participantId]) return;
//
//     const initiativeToAdd = { ...initiative, initiativeParticipation };
//
//     const groupProject = resultsGroups[participantId].projects[project.id] || {
//       ...project,
//       communicationInitiatives: [],
//       readinessInitiatives: [],
//       trainingInitiatives: [],
//       sustainmentInitiatives: [],
//       support:
//         participantType === 'Stakeholder'
//           ? initiativeParticipation.support
//           : undefined,
//       engagementFrequency:
//         participantType === 'Stakeholder'
//           ? initiativeParticipation.engagementFrequency
//           : undefined,
//     };
//
//     resultsGroups[participantId].projects[project.id] = {
//       ...groupProject,
//       [initiativeType]: [...groupProject[initiativeType], initiativeToAdd],
//     };
//   }
//
//   props.results.projects.forEach((project) => {
//     initiativeTypes.forEach((initiativeType) => {
//       project[initiativeType].forEach(
//         ({
//           initiativeParticipations,
//           stakeholderParticipations,
//           ...initiative
//         }) => {
//           const participations =
//             participantType === 'Stakeholder'
//               ? stakeholderParticipations || []
//               : initiativeParticipations || [];
//           participations.forEach((initiativeParticipation) => {
//             addInitiative(
//               initiativeParticipation,
//               project,
//               initiativeType,
//               initiative
//             );
//           });
//         }
//       );
//     });
//   });
//
//   const stakeholdersWithProjects = {};
//
//   props.results.projects.forEach((project) => {
//     (project.stakeholderProfiles || []).forEach((stakeholderProfile) => {
//       const stakeholderId = stakeholderProfile.organizationStakeholderId;
//
//       if (resultsGroups[stakeholderId]) {
//         if (!stakeholdersWithProjects[stakeholderId]) {
//           stakeholdersWithProjects[stakeholderId] = {
//             id: stakeholderId,
//             participantType: 'Stakeholder',
//             projects: [],
//           };
//         }
//
//         const projectToAdd = {
//           ...project,
//           communicationInitiatives: [],
//           readinessInitiatives: [],
//           trainingInitiatives: [],
//           sustainmentInitiatives: [],
//           support:
//             participantType === 'Stakeholder'
//               ? stakeholderProfile.support
//               : undefined,
//           engagementFrequency:
//             participantType === 'Stakeholder'
//               ? stakeholderProfile.engagementFrequency
//               : undefined,
//         };
//
//         stakeholdersWithProjects[stakeholderId].projects.push(projectToAdd);
//       }
//     });
//   });
//
//   Object.keys(stakeholdersWithProjects).forEach((stakeholderId) => {
//     if (!resultsGroups[stakeholderId]) {
//       resultsGroups[stakeholderId] = stakeholdersWithProjects[stakeholderId];
//     } else {
//       resultsGroups[stakeholderId].projects = [
//         ...Object.values(resultsGroups[stakeholderId].projects),
//         ...stakeholdersWithProjects[stakeholderId].projects,
//       ];
//     }
//   });
//   return map(resultsGroups, (group) => ({
//     ...group,
//     projects: Object.values(group.projects),
//   })).filter(({ projects }) => projects.length);
// }
